import {
  Button,
  Container,
  Grid,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconArrowRight,
  IconCheck,
  IconClockHour4,
  IconFileText,
  IconFlask2,
  IconLeaf,
  IconMessageCircle,
  IconMicroscope,
  IconNotebook,
  IconRosetteDiscountCheck,
  IconShieldCheck,
  IconStarFilled,
  IconUserCheck,
} from "@tabler/icons-react";
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { buildWhatsAppUrl } from "../../../Services/contact.service";
import {
  getAllReviews,
  getReviewsByAssignmentSlug,
  getReviewSummary,
} from "../../../Services/review.service";
import { getAssignmentPageBySlug } from "../../../data/assignmentPages";
import Seo from "../../../seo/Seo";
import {
  createBreadcrumbSchema,
  createFaqSchema,
  createOrganizationSchema,
} from "../../../seo/utils";
import { getAssignmentPagePath } from "../../../utils/routes";
import AssignmentFAQ from "../AssignmentFAQ";
import Breadcrumbs from "../Breadcrumbs";
import GetHelpForm from "../../Shared/GetHelpForm";
import ReviewsPreviewSection from "../../Reviews/ReviewsPreviewSection";
import "../BotanyAssignmentPage2.css";

const pageSlug = "last-minute-assignment-help";

const trustSignals = [
  {
    label: "Urgent Assignment Support",
    score: "4.9",
    icon: IconShieldCheck,
  },
  {
    label: "Student Rated Support",
    score: "4.8",
    icon: IconUserCheck,
  },
  {
    label: "Verified Review Signals",
    score: "4.9",
    icon: IconRosetteDiscountCheck,
  },
];

const scholarCards = [
  {
    name: "Rachel Morgan",
    degree: "Ph.D. in Academic Writing",
    initials: "RM",
    description:
      "Best for deadline triage, weak introductions, and essay drafts that need a faster path from confusion to a submit-ready structure.",
    completedOrders: 132,
    studentReviews: 54,
    stars: 5,
    accent: "botany-v2-avatar-one",
  },
  {
    name: "Daniel Price",
    degree: "MSc in Management and Coursework Support",
    initials: "DP",
    description:
      "Helps students prioritise high-mark sections first, especially for reports, case studies, and assignments that need a more logical flow under pressure.",
    completedOrders: 108,
    studentReviews: 41,
    stars: 4,
    accent: "botany-v2-avatar-two",
  },
  {
    name: "Leah Carter",
    degree: "MA in Research and Referencing Support",
    initials: "LC",
    description:
      "Strong at citation clean-up, paragraph restructuring, and turning rough notes into clearer academic writing close to the deadline.",
    completedOrders: 121,
    studentReviews: 49,
    stars: 5,
    accent: "botany-v2-avatar-three",
  },
  {
    name: "Ethan Brooks",
    degree: "MTech in Technical Assignment Guidance",
    initials: "EB",
    description:
      "Supports urgent technical and mixed-format tasks where students need help deciding what to fix, what to cut, and how to present ideas more clearly.",
    completedOrders: 97,
    studentReviews: 34,
    stars: 4,
    accent: "botany-v2-avatar-four",
  },
];

const processSteps = [
  {
    title: "Submit Your Requirements",
    description:
      "Send us your last-minute assignment prompt, deadline details, and any notes your professor has already shared.",
    icon: IconNotebook,
  },
  {
    title: "Secure Payment",
    description:
      "Our experts connect with you for order pricing discussion, Pay advance 10-30% to get your work started and the rest after work completion.",
    icon: IconShieldCheck,
  },
  {
    title: "Receive Your Order",
    description:
      "Get your finished last-minute work before the due date with clear structure, better explanation, and personal tutor support.",
    icon: IconFileText,
  },
];

const featurePillars = [
  {
    title: "Original Writing",
    description:
      "We build every last-minute response around the exact task so the structure and explanation feel specific instead of generic.",
    icon: IconShieldCheck,
  },
  {
    title: "Day and Night Support",
    description:
      "You can share your brief, ask follow-up questions, and get direction whenever the assignment starts feeling unclear.",
    icon: IconMicroscope,
  },
  {
    title: "Accurate Subject Data",
    description:
      "Assignment details, terminology, and evidence are checked carefully so the work reads more confidently and correctly.",
    icon: IconNotebook,
  },
  {
    title: "Correct Citations",
    description:
      "Useful for reports and essays that lose marks because references, in-text citations, or formatting feel inconsistent.",
    icon: IconClockHour4,
  },
  {
    title: "Fast Delivery",
    description:
      "When deadlines are close, we focus on the changes that improve clarity and quality fastest without wasting effort.",
    icon: IconFlask2,
  },
  {
    title: "Free Changes",
    description:
      "If a section still needs improvement after review, you can refine it further instead of feeling locked into one draft path.",
    icon: IconLeaf,
  },
];

const reviewCards = [
  {
    title: "Business",
    meta: "Urgent report support, university student",
    quote:
      "The support helped me stop panicking and focus on the sections that actually mattered. I submitted a much cleaner report than I expected.",
    date: "12 Oct 2025",
  },
  {
    title: "Computer Science",
    meta: "Deadline triage, university student",
    quote:
      "I had very little time left, but the feedback was practical and specific. It helped me fix structure and explain my work without overediting.",
    date: "04 Oct 2025",
  },
  {
    title: "Nursing",
    meta: "Draft improvement, university student",
    quote:
      "Clear comments, faster direction, and much less confusion. I finally knew what to prioritise before submitting the final draft.",
    date: "29 Sep 2025",
  },
];

const serviceTopics = [
  "Urgent essay structure help",
  "Last-minute report planning",
  "Case study analysis support",
  "Draft clean-up before submission",
  "Referencing and citation checks",
  "Word-count trimming and focus",
  "Presentation and slide direction",
  "Coding and technical task review",
  "Introduction and conclusion rescue",
  "Submission-readiness checks",
];

const freeSupportItems = [
  "Deadline triage plan",
  "Structure feedback",
  "Priority edit notes",
  "Reference checks",
  "Formatting review",
  "Intro and conclusion cleanup",
  "Submission checklist",
];
const contentSections = [
  {
    title:
      "Why Last-Minute Assignment Support Can Still Make a Real Difference",
    paragraphs: [
      "A close deadline does not always mean the assignment is beyond saving. Many students still have enough time to improve structure, answer the brief more clearly, fix weak sections, and submit work that reads more confidently.",
      "Last-minute assignment support works best when it focuses on priority decisions. Instead of trying to perfect everything, students need help understanding what matters most, what can wait, and what changes will improve the submission fastest.",
    ],
    bullets: [
      "Clearer decisions about what to write first",
      "More organised structure under time pressure",
      "Faster movement from panic to practical action",
    ],
  },
  {
    title: "Common Problems Students Face Near the Deadline",
    paragraphs: [
      "Most last-minute assignments are not blocked by one single issue. Students often face a combination of unclear instructions, missing structure, weak references, limited time, and uncertainty about what the marker expects.",
      "Another common problem is overthinking. Students may spend too long trying to make the opening perfect while the main body remains unfinished, or they may keep researching without turning ideas into actual paragraphs.",
    ],
    bullets: [
      "Misreading the command word or scope of the question",
      "Spending too much time on formatting before the main answer is done",
      "Missing citations, conclusions, or essential submission checks",
    ],
  },
  {
    title: "How SubjectBuddy Helps With Last-Minute Assignment Help",
    paragraphs: [
      "SubjectBuddy provides human-led last minute assignment help for students who need clear, fast, and practical academic guidance before submission. The goal is to improve understanding, structure, and presentation without making the process feel more overwhelming.",
      "We help students break down the brief, prioritise sections, strengthen argument flow, and improve how ideas, evidence, and references are presented. This is useful for essays, reports, case studies, presentations, and mixed-format coursework.",
    ],
    bullets: [
      "Support with urgent coursework planning and draft improvement",
      "Faster clarity on structure, priorities, and academic direction",
      "Practical help with formatting, referencing, and final checks",
    ],
  },
  {
    title: "What to Prioritise First in a Last-Minute Assignment",
    paragraphs: [
      "When time is limited, not every task deserves the same attention. The strongest approach is to prioritise the parts that usually carry the most marks: answering the question directly, building a logical main body, using evidence properly, and closing with a clear conclusion.",
      "Students often save the wrong things for first, such as title-page formatting or overediting the opening paragraph. A stronger plan is to secure the core answer, then improve presentation and polish after the main discussion is complete.",
    ],
    bullets: [
      "Read the brief and identify the command word",
      "Finish the main body before over-polishing early sections",
      "Add citations and a conclusion before final formatting passes",
    ],
  },
  {
    title: "How to Build a Fast but Clear Assignment Structure",
    paragraphs: [
      "Even when the deadline is close, a clear structure can make the assignment feel more organised and more credible. Most tasks become easier to manage once they are broken into a short introduction, a few main points, a conclusion, and a working reference list.",
      "For reports, reflections, or case studies, that structure may change slightly, but the same principle still applies: every section should have a clear purpose and connect back to the question. Good structure is often the fastest visible upgrade a draft can get.",
    ],
    bullets: [
      "Use headings when the brief allows them",
      "Build paragraphs around one clear point each",
      "Keep transitions simple and directly linked to the task",
    ],
  },
  {
    title: "Research and Referencing Under Time Pressure",
    paragraphs: [
      "Students under pressure often lose time by reading too widely instead of gathering a few useful sources that directly support their main points. Last-minute research should be selective, targeted, and tied closely to the question.",
      "Referencing also becomes more important when time is short because missing citations are easy marks lost. A small amount of organised source use usually works better than trying to force too many references into a rushed draft.",
    ],
    bullets: [
      "Choose sources that clearly support your main claims",
      "Add citations while writing instead of leaving them for last",
      "Check reference list consistency before submission",
    ],
  },
  {
    title: "What to Do When Only a Few Hours Are Left",
    paragraphs: [
      "If the deadline is very close, the target should shift from perfection to completion and clarity. Students usually benefit most from finishing the core argument, tightening the most visible weak spots, and checking that the file is ready to submit correctly.",
      "This stage is about disciplined choices. It is better to submit a complete, relevant assignment with simple language and solid structure than a more ambitious draft with missing sections, no conclusion, or broken references.",
    ],
    bullets: [
      "Finish the answer before attempting full perfection",
      "Check file name, formatting, and missing sections",
      "Make sure the submission still answers the exact question asked",
    ],
  },
  {
    title: "Common Last-Minute Mistakes That Reduce Quality",
    paragraphs: [
      "Many rushed assignments lose marks for avoidable reasons rather than deep subject weakness. Students may ignore the rubric, describe too much without analysing, forget the conclusion, or submit without checking references and paragraph flow.",
      "Another common mistake is trying to rewrite everything at once. That usually creates more confusion. The stronger approach is to identify the biggest problems, solve those first, and leave smaller improvements for the end if time remains.",
    ],
    bullets: [
      "Do not copy source material directly into the draft",
      "Do not leave citations and references until the final minute",
      "Do not confuse a longer draft with a stronger answer",
    ],
  },
  {
    title: "Why Students Choose SubjectBuddy for Last-Minute Assignment Help",
    paragraphs: [
      "Students choose SubjectBuddy when they want focused guidance rather than generic advice. The aim is to help you make better academic decisions quickly, especially when there is no time to waste on broad or unclear suggestions.",
      "Our support is built around understanding the brief, improving structure, identifying the highest-impact changes, and helping you submit something clearer, stronger, and more manageable under pressure.",
    ],
    bullets: [
      "Faster progress on the parts that matter most",
      "Clearer next steps instead of vague general feedback",
      "Calmer, more practical support close to the deadline",
    ],
  },
  {
    title: "Get Online Last-Minute Assignment Help Today",
    paragraphs: [
      "If your assignment deadline is close, the most useful first step is to share the brief, due time, and current draft status clearly. That makes it easier to focus on the practical changes that can still improve the submission.",
      "Whether you need help with planning, structure, analysis, referencing, or final clean-up, SubjectBuddy can help you use the remaining time more effectively and submit with more confidence.",
    ],
    bullets: [
      "Share the brief, rubric, deadline, and your current progress",
      "Get guidance for structure, clarity, and submission readiness",
      "Use the time left in a smarter and more focused way",
    ],
  },
];

const sampleSection = {
  title: "Sample Academic Direction for a Last-Minute Assignment",
  heading: "How to strengthen a rushed management assignment answer",
  copy: "A strong last-minute assignment answer should not try to cover every possible idea. It should focus on the exact question, select two or three strong points, and explain them clearly with relevant evidence. For example, if the topic is leadership during organisational change, a better answer is not a long list of leadership styles. A better answer explains which leadership approach fits the case, why it matters for staff motivation or communication, what evidence supports that choice, and what limitations should also be acknowledged. This kind of focused structure usually reads more critically, even when the draft was written under time pressure. The goal is not perfect coverage. The goal is a clear, relevant, and defensible academic response.",
};

const lastMinuteFaqs = [
  {
    question:
      "Can I get help with a last minute case study, report, or coursework task?",
    answer:
      "Yes. Support can focus on understanding the brief, organising the structure, improving your main points, and making the final submission clearer and more relevant.",
  },
  {
    question: "Can you help me understand my last minute assignment brief?",
    answer:
      "Yes. Many students first need help breaking down the question, the command words, and the marking criteria so they know what to prioritise with the time left.",
  },
  {
    question: "Do you support urgent last minute assignment help?",
    answer:
      "Yes, depending on the deadline and task complexity. The fastest way to get useful support is to share the brief, due time, and the exact part that feels most difficult.",
  },
  {
    question: "Can you help improve structure and referencing?",
    answer:
      "Yes. Support can include paragraph flow, stronger ordering of ideas, cleaner analysis, and checking how citations or references are used across the draft.",
  },
  {
    question:
      "What should I share before asking for last minute assignment help?",
    answer:
      "Share your assignment brief, marking rubric, deadline, subject area, word count, referencing style, and any notes or draft sections you already have.",
  },
];

function LastMinuteAssignmentPage() {
  const theme = useMantineTheme();
  const desktop = useMediaQuery(`(min-width: ${theme.breakpoints.md})`);
  const { hash } = useLocation();
  const page = getAssignmentPageBySlug(pageSlug);

  useEffect(() => {
    if (hash) {
      window.requestAnimationFrame(() => {
        const target = document.getElementById(hash.replace(/^#/, ""));

        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });

      return;
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [hash]);

  if (!page) {
    return null;
  }

  const whatsappUrl = buildWhatsAppUrl(
    "Hi, I need help with my last minute assignment.",
  );
  const reviewSummary = getReviewSummary();
  const relatedReviews = getReviewsByAssignmentSlug(page.slug, 3);
  const fallbackReviews = getAllReviews().slice(0, 3);
  const orderPagePath = `/order-form?from=${page.slug}`;

  const seoTitle =
    page.metaTitle || "Last Minute Assignment Help | SubjectBuddy";
  const seoDescription =
    page.metaDescription ||
    "Get last minute assignment help for essays, reports, case studies, coursework planning, and study support with clear academic guidance from SubjectBuddy.";

  return (
    <main className="botany-v2-page">
      <Seo
        title={seoTitle}
        description={seoDescription}
        path={getAssignmentPagePath(page.slug)}
        schema={[
          createOrganizationSchema(),
          createFaqSchema(lastMinuteFaqs),
          createBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: page.title, path: getAssignmentPagePath(page.slug) },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: page.title,
            provider: {
              "@type": "Organization",
              name: process.env.REACT_APP_NAME || "SubjectBuddy",
            },
            serviceType: "Last minute assignment help",
            areaServed: [
              "United States",
              "United Kingdom",
              "Canada",
              "Australia",
            ],
            description: seoDescription,
          },
        ]}
      />

      <Container size="xl" className="botany-v2-shell">
        <section className="botany-v2-hero">
          <Breadcrumbs
            items={[{ label: "Home", href: "/" }, { label: page.title }]}
          />

          <div className="botany-v2-hero-grid">
            <div className="botany-v2-hero-copy">
              <Title order={1} className="botany-v2-title">
                Need Last Minute Assignment Help? Fast, Clear Support for
                Students
              </Title>
              <Text className="botany-v2-subtitle">
                Running close to the deadline does not mean you have to guess
                your way through the final draft. SubjectBuddy provides
                human-led last minute assignment help that focuses on structure,
                priorities, referencing, and the practical next steps that can
                still improve your work before submission.
              </Text>

              <div className="botany-v2-trust">
                Trusted by <strong>{reviewSummary.totalReviews}+</strong>{" "}
                students seeking clearer assignment support
              </div>

              <div className="botany-v2-trust-row">
                {trustSignals.map((item) => (
                  <div key={item.label} className="botany-v2-trust-card">
                    <div className="botany-v2-trust-head">
                      <ThemeIcon
                        size={34}
                        radius="xl"
                        color="orange"
                        variant="light"
                      >
                        <item.icon size={18} />
                      </ThemeIcon>
                      <div>
                        <div className="botany-v2-trust-label">
                          {item.label}
                        </div>
                        <div className="botany-v2-stars">
                          <IconStarFilled size={14} />
                          <IconStarFilled size={14} />
                          <IconStarFilled size={14} />
                          <IconStarFilled size={14} />
                          <IconStarFilled size={14} />
                          <span>{item.score}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="botany-v2-form-shell">
              <Paper
                className="botany-v2-form-card"
                radius="xl"
                p={desktop ? "1.5rem" : "1rem"}
              >
                <Stack gap="md">
                  <Title order={3} ta="center" className="botany-v2-form-title">
                    Struggling with a last-minute assignment?
                  </Title>
                  <div className="botany-v2-form-badges">
                    <span>
                      <IconCheck size={14} /> Clearer priorities
                    </span>
                    <span>
                      <IconCheck size={14} /> No vague feedback
                    </span>
                    <span>
                      <IconCheck size={14} /> Faster turnaround
                    </span>
                  </div>
                  <GetHelpForm
                    formSize="sm"
                    flatForm
                    headerMode="minimal"
                    submitLabel="Get Last-Minute Help"
                  />
                </Stack>
              </Paper>
            </div>
          </div>
        </section>

        <section className="botany-v2-experts">
          <div className="botany-v2-experts-shell">
            <div className="botany-v2-section-heading botany-v2-section-heading-center">
              <Title order={2}>
                Meet the Experts Supporting Last-Minute Assignment Help
              </Title>
              <Text>
                Our team includes real academic mentors who know how to reduce
                confusion, improve structure, and help students act faster when
                the deadline is close.
              </Text>
            </div>

            <SimpleGrid cols={{ base: 1, md: 2, xl: 4 }} spacing="lg">
              {scholarCards.map((card) => (
                <Paper
                  key={card.name}
                  className="botany-v2-scholar-card"
                  radius="xl"
                  p="lg"
                >
                  <Stack gap="md" h="100%">
                    <Group align="flex-start" wrap="nowrap">
                      <div className={`botany-v2-avatar ${card.accent}`}>
                        <span>{card.initials}</span>
                        <span className="botany-v2-avatar-status" />
                      </div>
                      <div>
                        <Text fw={900}>{card.name}</Text>
                        <Text size="sm" c="dimmed">
                          {card.degree}
                        </Text>
                        <div className="botany-v2-scholar-stars">
                          {Array.from({ length: 5 }).map((_, index) => (
                            <IconStarFilled
                              key={`${card.name}-${index}`}
                              size={14}
                              className={
                                index < card.stars
                                  ? "botany-v2-star-on"
                                  : "botany-v2-star-off"
                              }
                            />
                          ))}
                        </div>
                      </div>
                    </Group>

                    <Text size="sm" style={{ lineHeight: 1.8 }}>
                      {card.description}
                    </Text>

                    <div className="botany-v2-scholar-footer">
                      <div className="botany-v2-scholar-stats">
                        <span>
                          <IconFileText size={13} />
                          <strong>{card.completedOrders}</strong> Completed
                          Orders
                        </span>
                        <span>
                          <IconMessageCircle size={13} />
                          <strong>{card.studentReviews}</strong> Student Reviews
                        </span>
                      </div>

                      <Button
                        component="a"
                        href={whatsappUrl}
                        radius="md"
                        variant="outline"
                        color="green"
                      >
                        Get Help
                      </Button>
                    </div>
                  </Stack>
                </Paper>
              ))}
            </SimpleGrid>
          </div>
        </section>

        <section className="botany-v2-process">
          <div className="botany-v2-section-heading botany-v2-section-heading-center">
            <Title order={2}>
              From Seed to Success: How to Secure Your Professional
              Last-Minute Assignment
            </Title>
          </div>

          <div className="botany-v2-process-flow">
            {processSteps.map((step, index) => (
              <div key={step.title} className="botany-v2-process-node">
                <div className="botany-v2-process-item">
                  <div className="botany-v2-process-icon-shell">
                    <step.icon size={56} stroke={1.5} />
                  </div>
                  <Title order={3}>{step.title}</Title>
                  <Text c="dimmed" size="sm" style={{ lineHeight: 1.8 }}>
                    {step.description}
                  </Text>
                </div>

                {index < processSteps.length - 1 ? (
                  <div className="botany-v2-process-arrow" aria-hidden="true">
                    <IconArrowRight size={44} stroke={2.2} />
                  </div>
                ) : null}
              </div>
            ))}
          </div>

          <div className="botany-v2-process-cta">
            <Button
              component={Link}
              to={orderPagePath}
              color="green"
              radius="md"
              size="md"
            >
              Order Now
            </Button>
          </div>
        </section>

        <section className="botany-v2-features">
          <div className="botany-v2-section-heading botany-v2-section-heading-center">
            <Title order={2}>
              Why We Provide the Best Last-Minute Assignment Help in the
              United States
            </Title>
            <Text>
              We make sure your assignment work is done correctly. We focus on
              quality, speed, and facts.
            </Text>
          </div>

          <SimpleGrid cols={{ base: 1, md: 2, lg: 3 }} spacing="xl">
            {featurePillars.map((item) => (
              <div key={item.title} className="botany-v2-feature-row">
                <ThemeIcon
                  size={64}
                  radius="xl"
                  color="green"
                  variant="light"
                  className="botany-v2-feature-icon"
                >
                  <item.icon size={28} />
                </ThemeIcon>
                <Stack gap={6} className="botany-v2-feature-copy">
                  <Title order={3}>{item.title}</Title>
                  <Text c="dimmed" size="sm" style={{ lineHeight: 1.8 }}>
                    {item.description}
                  </Text>
                </Stack>
              </div>
            ))}
          </SimpleGrid>

          <div className="botany-v2-feature-cta">
            <Button
              component={Link}
              to={orderPagePath}
              radius="md"
              color="green"
              size="md"
            >
              Order Now
            </Button>
          </div>
        </section>

        <section className="botany-v2-reviews">
          <div className="botany-v2-section-heading botany-v2-section-heading-center">
            <Title order={2}>
              What Students Say About Our Last-Minute Assignment Help
            </Title>
            <Text>
              Reviews from students who needed faster direction, clearer edits,
              and more confidence before submission.
            </Text>
          </div>

          <div className="botany-v2-review-platforms">
            {trustSignals.map((item) => (
              <div key={item.label} className="botany-v2-platform-pill">
                <item.icon size={16} />
                <span>{item.label}</span>
              </div>
            ))}
          </div>

          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
            {reviewCards.map((review) => (
              <Paper
                key={review.title}
                className="botany-v2-review-card"
                radius="lg"
                p="lg"
              >
                <Stack gap="sm">
                  <div className="botany-v2-stars">
                    <IconStarFilled size={14} />
                    <IconStarFilled size={14} />
                    <IconStarFilled size={14} />
                    <IconStarFilled size={14} />
                    <IconStarFilled size={14} />
                  </div>
                  <Text fw={800}>{review.title}</Text>
                  <Text size="xs" c="dimmed">
                    {review.meta}
                  </Text>
                  <Text size="sm" c="dimmed" style={{ lineHeight: 1.8 }}>
                    {review.quote}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {review.date}
                  </Text>
                </Stack>
              </Paper>
            ))}
          </SimpleGrid>
        </section>

        <section className="botany-v2-urgent">
          <Paper
            className="botany-v2-urgent-card"
            radius="xl"
            p={desktop ? "2rem" : "1.25rem"}
          >
            <Grid align="center" gutter="xl">
              <Grid.Col span={{ base: 12, md: 4 }}>
                <div className="botany-v2-urgent-visual">
                  <IconNotebook size={84} />
                  <IconClockHour4
                    size={34}
                    className="botany-v2-float botany-v2-float-one"
                  />
                  <IconFileText
                    size={30}
                    className="botany-v2-float botany-v2-float-two"
                  />
                  <IconShieldCheck
                    size={30}
                    className="botany-v2-float botany-v2-float-three"
                  />
                </div>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 8 }}>
                <Stack gap="md">
                  <Title order={2}>
                    Get Urgent Last-Minute Assignment Help for Same-Day
                    Deadlines
                  </Title>
                  <Text c="dimmed" style={{ lineHeight: 1.8 }}>
                    Access urgent support for essays, reports, case studies, and
                    draft improvement when the deadline is close and the next
                    steps need to become clearer fast.
                  </Text>
                  <Button
                    component={Link}
                    to={orderPagePath}
                    radius="md"
                    color="green"
                    leftSection={<IconClockHour4 size={18} />}
                    className="botany-v2-urgent-button"
                  >
                    Order Support
                  </Button>
                </Stack>
              </Grid.Col>
            </Grid>
          </Paper>
        </section>

        <section className="botany-v2-services">
          <div className="botany-v2-services-grid">
            <div className="botany-v2-services-left">
              <Title order={2}>
                Our Last-Minute Assignment Support Covers Every Academic Need
              </Title>
              <Text c="dimmed" style={{ lineHeight: 1.8 }}>
                SubjectBuddy supports essays, reports, presentations, case
                studies, and technical coursework that need clearer structure,
                faster direction, and more confident academic flow before
                submission.
              </Text>
              <Text fw={800}>Type of support</Text>
              <ul className="botany-v2-service-list">
                {serviceTopics.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="botany-v2-services-right">
              <Paper className="botany-v2-freebies-card" radius="xl" p="lg">
                <Stack gap="md">
                  <Text fw={900} tt="uppercase" size="sm" c="orange">
                    Included support areas
                  </Text>
                  {freeSupportItems.map((item) => (
                    <div key={item} className="botany-v2-freebie-row">
                      <span className="botany-v2-freebie-label">
                        <IconCheck size={16} />
                        {item}
                      </span>
                      <span className="botany-v2-freebie-value">Included</span>
                    </div>
                  ))}
                  <Button
                    component="a"
                    href={whatsappUrl}
                    radius="md"
                    color="orange"
                  >
                    Get Support
                  </Button>
                </Stack>
              </Paper>
            </div>
          </div>
        </section>

        <section className="botany-v2-content">
          <div className="botany-v2-content-scroll">
            <div className="botany-v2-content-grid">
              {contentSections.map((section) => (
                <article key={section.title} className="botany-v2-article-card">
                  <Stack gap="md">
                    <Title order={2}>{section.title}</Title>
                    {section.paragraphs.map((paragraph) => (
                      <Text
                        key={paragraph}
                        c="dimmed"
                        style={{ lineHeight: 1.9 }}
                      >
                        {paragraph}
                      </Text>
                    ))}
                    <ul className="botany-v2-article-list">
                      {section.bullets.map((bullet) => (
                        <li key={bullet}>{bullet}</li>
                      ))}
                    </ul>
                  </Stack>
                </article>
              ))}

              <article className="botany-v2-article-card">
                <Stack gap="md">
                  <Title order={2}>{sampleSection.title}</Title>
                  <Paper className="botany-v2-sample-card" radius="lg" p="lg">
                    <Stack gap="sm">
                      <Title order={3}>{sampleSection.heading}</Title>
                      <Text c="dimmed" style={{ lineHeight: 1.85 }}>
                        {sampleSection.copy}
                      </Text>
                    </Stack>
                  </Paper>
                </Stack>
              </article>
            </div>
          </div>
        </section>

        <section className="botany-v2-live-reviews">
          <ReviewsPreviewSection
            title="Recent Student Feedback on Last-Minute Support"
            description="Selected student feedback that reflects clearer priorities, stronger structure, and calmer deadline support."
            reviews={relatedReviews.length ? relatedReviews : fallbackReviews}
            compactCards
            plainTrustBar
          />
        </section>

        <section className="botany-v2-faqs">
          <div className="botany-v2-section-heading">
            <Title order={2}>FAQs on Last-Minute Assignment Help</Title>
          </div>
          <AssignmentFAQ faqs={lastMinuteFaqs} />
        </section>
      </Container>
    </main>
  );
}

export default LastMinuteAssignmentPage;
